// ============================================
// Scenario Parser — Fashion Plate v2
//
// Speak-to-dress service: parses natural language
// patient scenarios into structured context variables
// and garment states via Gemini.
// ============================================

import { geminiGenerate } from '../ai/providers/gemini';
import type { ContextLayer } from '../../types/patientJourney';
import type { GarmentState, GarmentPiece } from '../../types/fashionPlate';
import { GARMENT_REGISTRY, resolveGarmentLabel } from '../../data/fashionPlate/garmentRegistry';

const MODEL = 'gemini-2.5-flash';
const TEMPERATURE = 0.3;
const MAX_TOKENS = 1024;

const GARMENT_PIECES: GarmentPiece[] = ['shoes', 'heart', 'brain', 'hat', 'dress', 'accessories'];

const SYSTEM_PROMPT = `You are a clinical context extraction engine for an AI nurse persona system.

Given a natural language patient scenario, extract the following 17 context variables and a creative persona name.

Return ONLY valid JSON with no markdown fencing, no explanation. The JSON must have this exact shape:

{
  "personaName": "<creative 2-3 word name like 'The Night Nurse' or 'The Anxious Overachiever'>",
  "timeOfDay": "morning" | "afternoon" | "evening" | "late_night",
  "cyclePhase": "follicular" | "ovulatory" | "luteal" | "menstrual" | "postmenopausal" | "not_applicable",
  "season": "spring" | "summer" | "fall" | "winter",
  "timeSinceLastInteraction": "first_visit" | "days" | "weeks" | "months",
  "baselineStress": "low" | "moderate" | "high" | "crisis",
  "recentLifeEvent": "none" | "divorce" | "job_loss" | "bereavement" | "new_diagnosis" | "new_baby" | "relocation",
  "motivation": "desperate" | "concerned" | "curious" | "proactive" | "skeptical",
  "partnerStatus": "single" | "partnered" | "divorced" | "widowed",
  "caregiverBurden": "none" | "light" | "heavy",
  "socialSupport": "strong" | "moderate" | "isolated",
  "budgetSensitivity": "low" | "moderate" | "high" | "severe",
  "insuranceContext": "insured" | "uninsured" | "hsa_fsa_eligible",
  "healthKnowledge": "minimal" | "basic" | "informed" | "expert",
  "supplementExperience": "none" | "some" | "extensive",
  "aiComfort": "distrustful" | "cautious" | "neutral" | "comfortable",
  "journeyStage": "first_visit" | "on_protocol" | "retesting" | "post_escalation" | "re_engagement",
  "adherenceLevel": "high" | "moderate" | "low" | "unknown"
}

Rules:
- Only use the exact allowed values listed above for each field.
- If the scenario does not mention a variable, make your best inference from context clues.
- The personaName should be evocative and capture the essence of the patient (e.g. "The Midnight Caregiver", "The Skeptical Scientist", "The Burned-Out Mom").
- Return ONLY the JSON object, no other text.`;

const CONTEXT_KEYS: (keyof ContextLayer)[] = [
  'timeOfDay',
  'cyclePhase',
  'season',
  'timeSinceLastInteraction',
  'baselineStress',
  'recentLifeEvent',
  'motivation',
  'partnerStatus',
  'caregiverBurden',
  'socialSupport',
  'budgetSensitivity',
  'insuranceContext',
  'healthKnowledge',
  'supplementExperience',
  'aiComfort',
  'journeyStage',
  'adherenceLevel',
];

/**
 * Build default garment states when parsing fails.
 * Uses empty context so each piece falls back to its first variant.
 */
function buildDefaultGarments(): GarmentState[] {
  return GARMENT_PIECES.map((piece) => {
    const entry = GARMENT_REGISTRY.find((e) => e.piece === piece);
    const { label, intensity } = resolveGarmentLabel(piece, {});
    return {
      piece,
      label,
      intensity,
      contextCategory: entry?.contextCategory ?? 'Unknown',
      activeVariables: {},
    };
  });
}

/**
 * Parse a natural language patient scenario into structured context
 * variables and resolved garment states via Gemini.
 */
export async function parseScenario(
  scenarioText: string,
): Promise<{ context: Partial<ContextLayer>; garments: GarmentState[]; name: string }> {
  const rawResponse = await geminiGenerate(
    MODEL,
    SYSTEM_PROMPT,
    scenarioText,
    TEMPERATURE,
    MAX_TOKENS,
  );

  // Strip markdown code fences if Gemini wraps the JSON
  const cleaned = rawResponse
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();

  let parsed: Record<string, string>;
  try {
    parsed = JSON.parse(cleaned);
  } catch {
    // Parse failure — return empty context with default garments
    return {
      context: {},
      garments: buildDefaultGarments(),
      name: 'Unknown Persona',
    };
  }

  // Extract persona name, then build context from remaining keys
  const personaName = parsed.personaName ?? 'Unknown Persona';
  const context: Partial<ContextLayer> = {};

  for (const key of CONTEXT_KEYS) {
    if (key in parsed && parsed[key] !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (context as any)[key] = parsed[key];
    }
  }

  // Resolve garment states from context
  const garments: GarmentState[] = GARMENT_PIECES.map((piece) => {
    const { label, intensity } = resolveGarmentLabel(piece, context);

    // Find the registry entry for this piece
    const entry = GARMENT_REGISTRY.find((e) => e.piece === piece);

    // Build activeVariables from context values that match this piece's variables
    const activeVariables: Record<string, string> = {};
    if (entry) {
      for (const varKey of entry.variables) {
        const value = context[varKey as keyof ContextLayer];
        if (value !== undefined) {
          activeVariables[varKey] = value;
        }
      }
    }

    return {
      piece,
      label,
      intensity,
      contextCategory: entry?.contextCategory ?? 'Unknown',
      activeVariables,
    };
  });

  return {
    context,
    garments,
    name: personaName,
  };
}
